/**
 * i18n conventions: UI strings live in `common` (shared), `pages.*` in pages.json, API codes in `errors.*`.
 * Auth toasts use `toastAuth.*`. Prefer nested keys over scattered literals.
 */
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import axios from 'axios';
import arCommon from '../locales/ar/common.json';
import arPages from '../locales/ar/pages.json';
import arErrors from '../locales/ar/errors.json';
import enCommon from '../locales/en/common.json';
import enPages from '../locales/en/pages.json';
import enErrors from '../locales/en/errors.json';

const ar = { ...arCommon, pages: arPages, errors: arErrors };
const en = { ...enCommon, pages: enPages, errors: enErrors };

export const SUPPORTED_LANGS = ['ar', 'en'] as const;
export type AppLang = (typeof SUPPORTED_LANGS)[number];

export function isRtlLang(lng: string): boolean {
  return String(lng).split('-')[0] === 'ar';
}

function normalizeDetectedLanguage(lng: string): string {
  const base = String(lng || '').split('-')[0].toLowerCase();
  if (base === 'en') return 'en';
  if (base === 'ar') return 'ar';
  try {
    localStorage.setItem('oa_i18n_lang', 'ar');
  } catch {
    /* ignore */
  }
  return 'ar';
}

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      ar: { common: ar },
      en: { common: en }
    },
    fallbackLng: 'ar',
    lng: 'ar',
    defaultNS: 'common',
    ns: ['common'],
    interpolation: { escapeValue: false },
    detection: {
      order: ['localStorage', 'htmlTag'],
      caches: ['localStorage'],
      lookupLocalStorage: 'oa_i18n_lang',
      convertDetectedLanguage: normalizeDetectedLanguage
    },
    supportedLngs: [...SUPPORTED_LANGS],
    nonExplicitSupportedLngs: true,
    load: 'languageOnly',
    react: {
      useSuspense: false
    }
  });

function syncAxiosAcceptLanguage(lng: string) {
  axios.defaults.headers.common['Accept-Language'] = lng || 'ar';
}

syncAxiosAcceptLanguage(i18n.language);
i18n.on('languageChanged', (lng) => {
  syncAxiosAcceptLanguage(lng);
});

export default i18n;
